import React, { useState, useEffect } from 'react';
import { 
  Server, 
  FileCode, 
  Users, 
  Briefcase,
  Brain, 
  LineChart, 
  BarChart,
  GitBranch
} from 'lucide-react';

const SkillsVisualization = () => {
  const [activeCategory, setActiveCategory] = useState('methodology');
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  // Skill categories
  const categories = [
    { id: 'methodology', name: 'Methodologies', icon: <Users className="w-5 h-5" /> },
    { id: 'software', name: 'Software', icon: <FileCode className="w-5 h-5" /> },
    { id: 'leadership', name: 'Leadership', icon: <Briefcase className="w-5 h-5" /> },
    { id: 'technical', name: 'Technical', icon: <Server className="w-5 h-5" /> },
    { id: 'data', name: 'Data & Analytics', icon: <BarChart className="w-5 h-5" /> },
  ];

  // Skills data
  const skills = {
    methodology: [
      { name: 'Scrum', level: 95, description: 'Certified ScrumMaster with experience leading Agile teams' },
      { name: 'Kanban', level: 90, description: 'Implemented Kanban boards and workflows for multiple teams' },
      { name: 'SAFe', level: 85, description: 'Certified SAFe® Scrum Master' },
      { name: 'DevOps', level: 80, description: 'Implemented CI/CD pipelines and Agile DevOps practices' },
      { name: 'Lean', level: 75, description: 'Applied Lean principles to eliminate waste in processes' },
    ],
    software: [
      { name: 'Jira', level: 95, description: 'Advanced administration and workflow configuration' },
      { name: 'Salesforce', level: 90, description: 'CRM implementation, customization, and integration' },
      { name: 'Microsoft Azure DevOps', level: 80, description: 'Project management and CI/CD workflows' },
      { name: 'ServiceNow', level: 75, description: 'Workflow automation and service management' },
      { name: 'Bitbucket', level: 85, description: 'Version control and code management' },
    ],
    leadership: [
      { name: 'Team Leadership', level: 95, description: 'Led cross-functional teams of 5-12 members' },
      { name: 'Stakeholder Management', level: 90, description: 'Facilitated 100+ hours of stakeholder workshops' },
      { name: 'Strategic Planning', level: 85, description: 'Developed technology roadmaps and implementation plans' },
      { name: 'Change Management', level: 80, description: 'Guided organizations through digital transformation' },
      { name: 'Mentoring', level: 90, description: 'Coached teams on Agile methodologies and best practices' },
    ],
    technical: [
      { name: 'Salesforce', level: 90, description: 'Administration, Account Engagement, Sales Cloud' },
      { name: 'CRM Systems', level: 85, description: 'Implementation and integration of various CRM platforms' },
      { name: 'Authentication', level: 85, description: 'FIDO2-based systems and implementation' },
      { name: 'API Integration', level: 80, description: 'Connected systems through RESTful APIs' },
      { name: 'Solution Architecture', level: 85, description: 'Designed enterprise technical solutions' },
    ],
    data: [
      { name: 'Customer Data Platforms', level: 85, description: 'Strategy and implementation planning' },
      { name: 'Analytics', level: 80, description: 'Datorama, dashboard design, reporting frameworks' },
      { name: 'Data Strategy', level: 85, description: 'Roadmapping and capability development' },
      { name: 'AI/ML', level: 75, description: 'Use case development and assessment frameworks' },
      { name: 'Marketing Technology', level: 85, description: 'Pardot, Adobe Experience Platform, automation' },
    ],
  };

  // Get skills for active category
  const activeSkills = skills[activeCategory] || [];

  return (
    <div className={`min-h-screen bg-gray-50 py-16 transition-opacity duration-1000 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Skills & Expertise</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            A comprehensive view of my professional capabilities across various domains and technologies.
          </p>
        </div>

        {/* Category Tabs */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setActiveCategory(category.id)}
              className={`flex items-center px-4 py-3 rounded-lg transition-colors ${
                activeCategory === category.id
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              <span className="mr-2">{category.icon}</span>
              <span className="font-medium">{category.name}</span>
            </button>
          ))}
        </div>

        {/* Skills Visualization */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-8 pb-4 border-b border-gray-100">
            {categories.find(c => c.id === activeCategory)?.name}
          </h3>
          
          <div className="space-y-8">
            {activeSkills.map((skill, index) => (
              <div key={index} className="skill-item">
                <div className="flex justify-between items-center mb-2">
                  <h4 className="text-lg font-medium text-gray-800">{skill.name}</h4>
                  <span className="text-sm font-medium text-gray-600">{skill.level}%</span>
                </div>
                
                <div className="relative h-4 bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className="absolute top-0 left-0 h-full bg-blue-600 rounded-full"
                    style={{ 
                      width: `${skill.level}%`,
                      transition: 'width 1s ease-in-out',
                      animation: `growBar 1.5s ease-out forwards ${index * 0.1}s`
                    }}
                  ></div>
                </div>
                
                <p className="mt-1 text-sm text-gray-600">{skill.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Skills Matrix */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          <div className="bg-white rounded-lg shadow-md p-6 border-t-4 border-blue-600">
            <div className="flex items-center mb-4">
              <Brain className="w-5 h-5 text-blue-600 mr-2" />
              <h4 className="text-lg font-semibold text-gray-900">Problem Solving Approach</h4>
            </div>
            <ul className="space-y-2 text-gray-700">
              <li className="flex items-start">
                <span className="text-blue-600 mr-2 mt-1">•</span>
                <span>Analyze business requirements thoroughly</span>
              </li>
              <li className="flex items-start">
                <span className="text-blue-600 mr-2 mt-1">•</span>
                <span>Break complex problems into manageable components</span>
              </li>
              <li className="flex items-start">
                <span className="text-blue-600 mr-2 mt-1">•</span>
                <span>Balance technical constraints with business priorities</span>
              </li>
              <li className="flex items-start">
                <span className="text-blue-600 mr-2 mt-1">•</span>
                <span>Innovate within established frameworks</span>
              </li>
            </ul>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6 border-t-4 border-green-600">
            <div className="flex items-center mb-4">
              <GitBranch className="w-5 h-5 text-green-600 mr-2" />
              <h4 className="text-lg font-semibold text-gray-900">Technical Adaptability</h4>
            </div>
            <ul className="space-y-2 text-gray-700">
              <li className="flex items-start">
                <span className="text-green-600 mr-2 mt-1">•</span>
                <span>Quick adoption of new technologies</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-600 mr-2 mt-1">•</span>
                <span>Seamless integration between diverse systems</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-600 mr-2 mt-1">•</span>
                <span>Language and platform agnostic approach</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-600 mr-2 mt-1">•</span>
                <span>Self-directed learning and exploration</span>
              </li>
            </ul>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6 border-t-4 border-purple-600">
            <div className="flex items-center mb-4">
              <LineChart className="w-5 h-5 text-purple-600 mr-2" />
              <h4 className="text-lg font-semibold text-gray-900">Continuous Improvement</h4>
            </div>
            <ul className="space-y-2 text-gray-700">
              <li className="flex items-start">
                <span className="text-purple-600 mr-2 mt-1">•</span>
                <span>Regular retrospectives and lessons learned</span>
              </li>
              <li className="flex items-start">
                <span className="text-purple-600 mr-2 mt-1">•</span>
                <span>Data-driven optimization of processes</span>
              </li>
              <li className="flex items-start">
                <span className="text-purple-600 mr-2 mt-1">•</span>
                <span>Iterative enhancement of deliverables</span>
              </li>
              <li className="flex items-start">
                <span className="text-purple-600 mr-2 mt-1">•</span>
                <span>Knowledge sharing and team enablement</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Certifications */}
        <div className="text-center mb-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">Certifications</h3>
          <div className="flex flex-wrap justify-center gap-4">
            <div className="bg-blue-50 px-5 py-3 rounded-full text-blue-800 font-medium">PMP® Certified</div>
            <div className="bg-blue-50 px-5 py-3 rounded-full text-blue-800 font-medium">SAFe® Scrum Master</div>
            <div className="bg-blue-50 px-5 py-3 rounded-full text-blue-800 font-medium">Certified Scrum Master (CSM)</div>
            <div className="bg-blue-50 px-5 py-3 rounded-full text-blue-800 font-medium">Certified Scrum Product Owner (CSPO)</div>
            <div className="bg-blue-50 px-5 py-3 rounded-full text-blue-800 font-medium">Salesforce Certified Associate</div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="text-center">
          <a 
            href="/case-studies" 
            className="inline-flex items-center px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md transition-colors"
          >
            See Skills in Action
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              className="h-5 w-5 ml-2" 
              viewBox="0 0 20 20" 
              fill="currentColor"
            >
              <path 
                fillRule="evenodd" 
                d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z" 
                clipRule="evenodd" 
              />
            </svg>
          </a>
        </div>
      </div>

      <style jsx>{`
        @keyframes growBar {
          0% { width: 0; }
          100% { width: ${activeSkills.level}%; }
        }
      `}</style>
    </div>
  );
};

export default SkillsVisualization;